import React from "react";
import styles from "./postsSearchResults.module.css";

const PostsSearchResults = ({ postsResults, keywordsStr }) => {
  /* This component is responsible for displaying posts search results. It is used by the SearchPosts component on the Search page. For each post, the username and profile image of the user who published the post are shown, as well as the post date, and an extract from the post. There is a link to the profile page of the post publisher. */

  if (!postsResults) {
    return null;
  }

  /**
   * Visit the profile page of the post publisher, in a new tab.
   * @param {*} username
   */
  const visitProfile = (username) => {
    window.open(`/profile/${username}`, "_blank", "noopener,noreferrer");
  };

  /* This function gets two Strings - a post excerpt, and a string of keywords the user searched for. The function returns a string identical to the original post excerpt, with the following change: all words in the excerpt that also appear in the second string (keywords), are marked in bold. */
  function HighlightWords({ postExcerpt = "", keywordsStr = "" }) {
    if (!keywordsStr) {
      return <div>{postExcerpt}</div>;
    }

    // Convert both strings to lowercase for case-insensitive comparison
    const keywords = new Set(keywordsStr.toLowerCase().split(/\s+/));

    // Split the post excerpt into words and process each word
    const excerptWords = postExcerpt.split(/\s+/);

    const processedWords = excerptWords.map((word, index) => {
      // Check if the lowercase version of the word is in the set of keywords
      const cleanWord = word.replace(/[^\w\s]|_/g, "").toLowerCase(); // Removing punctuation and converting to lowercase
      if (keywords.has(cleanWord)) {
        return <strong key={index}>{word} </strong>; // Render the original word in bold
      } else {
        return word + " "; // Render the original word with a space
      }
    });

    return <div>"...{processedWords}"</div>;
  }

  /**
   * Convert a MySQL TIMESTAMP to a string representation of the date
   * @param {*} dateString a MySQL TIMESTAMP
   * @returns string - for example: "July 25, 2024"
   */
  const formatDate = (dateString) => {
    if (!dateString) {
      return "Invalid Date";
    }
    const date = new Date(dateString.replace(" ", "T"));
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    const options = { year: "numeric", month: "long", day: "numeric" };
    return date.toLocaleDateString(undefined, options);
  };

  /**
   * Prepare the display of the posts, given the postsResults array
   */
  const postsList = postsResults.map((post, idx) => (
    <div
      key={idx}
      className={styles.postResult}
      onClick={() => visitProfile(post.username)}
    >
      <figure>
        <img src={post.img_link} alt={post.username} />
        <figcaption>{post.username}</figcaption>
      </figure>

      <div className={styles.resultDate}>{formatDate(post.updated_at)}</div>

      <div className={styles.postExcerpt}>
        <HighlightWords postExcerpt={post.extract} keywordsStr={keywordsStr} />
      </div>
    </div>
  ));

  return (
    <div className={styles.searchResults}>
      <h2>Search Results</h2>
      {postsList.length > 0 && (
        <div>
          <p>The following posts match your search criteria:</p>
          {postsList}
        </div>
      )}
      {postsList.length === 0 && (
        <div>
          <p>No posts match your search criteria.</p>
        </div>
      )}
    </div>
  );
};

export default PostsSearchResults;
